/**
 * Account-level visibility flags shared by v1 and v2.
 *
 * Distinct from `FeatureGate`, which asks what a customer's *plan* entitles them
 * to. This asks whether a surface is ready to be shown at all — a staging area
 * for things that exist in the product but are not yet meant to be discovered.
 *
 * Deliberately outside `src/newui/`: that folder is written to be deletable in
 * one commit, and a flag v1 depends on cannot live somewhere designed to be
 * removed.
 *
 * These are client-side and cosmetic. They decide what a user is *shown*, never
 * what they may *do* — every endpoint behind them enforces its own permissions,
 * and a determined user can read this list. Anything that must actually be
 * prevented belongs in the backend, not here.
 */

/**
 * Accounts still shown the "Connect your CrowdVolt account" prompt.
 *
 * CrowdVolt works, but we are not inviting new customers into it yet, so the
 * invitation is hidden while the integration itself is left completely alone:
 * an account that is already connected keeps its CrowdVolt listings, its
 * linking and its List-for-Sale destination, and the Connections page still
 * reports its status. Only the prompt to start is gated.
 */
export const CROWDVOLT_CONNECT_ALLOWLIST: readonly string[] = ['isaac@amplifye.tech'];

export function canSeeCrowdVoltConnect(email?: string | null): boolean {
  if (!email) return false;
  return CROWDVOLT_CONNECT_ALLOWLIST.includes(email.trim().toLowerCase());
}

/**
 * Accounts shown the "QR code" action on a ticket (view + download the entry QR).
 *
 * Internal test window: free for these accounts while we decide whether it ships
 * as a $1-per-download feature. Widening this list is NOT how it launches — the
 * charge has to be enforced on the QR endpoint first, since this list is only
 * cosmetic (see the note at the top of this file).
 */
export const QR_DOWNLOAD_ALLOWLIST: readonly string[] = [
  'isaac@amplifye.tech',
  'support@flashtickets.fm',
  'david@flashtickets.fm',
  'davidwumailster@gmail.com',
  'dustin@flashtickets.fm',
  'dustinzhangzzz@gmail.com',
];

export function canDownloadTicketQr(email?: string | null): boolean {
  if (!email) return false;
  return QR_DOWNLOAD_ALLOWLIST.includes(email.trim().toLowerCase());
}
